import { useEffect, useRef } from 'react';

const SENSITIVITY = 0.8;

/**
 * Scrubs a <video> element forward/backward based on horizontal mouse movement.
 * Seek-flood prevention: queues next target and applies it once `onSeeked` fires.
 *
 * @param {React.RefObject<HTMLVideoElement>} videoRef
 */
export function useVideoScrub(videoRef) {
  const prevXRef = useRef(null);
  const targetTimeRef = useRef(0);
  const isSeekingRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    function seekTo(time) {
      isSeekingRef.current = true;
      video.currentTime = time;
    }

    function onSeeked() {
      isSeekingRef.current = false;
      if (Math.abs(video.currentTime - targetTimeRef.current) > 0.001) {
        seekTo(targetTimeRef.current);
      }
    }

    function onMouseMove(e) {
      if (!video.duration) return;

      const currentX = e.clientX;

      if (prevXRef.current === null) {
        prevXRef.current = currentX;
        return;
      }

      const delta = currentX - prevXRef.current;
      prevXRef.current = currentX;

      const offset = (delta / window.innerWidth) * SENSITIVITY * video.duration;
      const newTarget = Math.min(
        Math.max(targetTimeRef.current + offset, 0),
        video.duration
      );
      targetTimeRef.current = newTarget;

      if (!isSeekingRef.current) {
        seekTo(newTarget);
      }
    }

    video.addEventListener('seeked', onSeeked);
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => {
      video.removeEventListener('seeked', onSeeked);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [videoRef]);
}
